import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { WebCatalogProductResponse } from 'utils/services/VervAPI';
import Option from '../../option';
import activatedPromocodeImg from './img/activatedPromocode.png';

import s from './index.module.scss';
import {
	productPrice,
	productPricePerDay,
	pricePerDay,
	priceFirstPart,
	priceSecondPart,
} from '../helper';
import { Defaults, Localizations } from '../types';
import classNames from 'classnames';
import { PromocodeAnimated } from '../../promocodeAnimated';
import { appStore } from '@store';

interface Props {
	productID: string;
	isSelected?: boolean;
	isRadio?: boolean;
	isPrompted?: boolean;
	withPromocode?: boolean;
	withSpecialOffer?: boolean;
	promocode?: string;
	oldPricePercent?: number | undefined;
	hideDetailsPrice?: boolean | undefined;
	coloredSelectedBorder?: boolean;
	currencyType: string;
	defaults?: Defaults;
	save?: number;
	localizations: Localizations;
	promocodeAnimated?: Localizations;
	optionStyle?: string;
	oldFixedPrice?: number;
	showOldFixedPrice?: boolean;
	doOnClick?: () => void;
}

const PlanOption: FC<Props> = observer(props => {
	const { VervAPI, paymentStore } = appStore;
	const { settings, fixedDiscountPercent } = paymentStore;
	const { promocodeAnimated: promocodeAnimatedParam } = settings;

	const {
		productID,
		isSelected,
		isRadio,
		isPrompted,
		currencyType,
		defaults,
		localizations,
		save,
		doOnClick,
		withPromocode,
		promocode = 'VERV2023',
		promocodeAnimated = promocodeAnimatedParam,
		coloredSelectedBorder,
		withSpecialOffer,
		hideDetailsPrice,
		optionStyle,
		oldFixedPrice,
		showOldFixedPrice,
	} = props;
	const [product, setProduct] = useState<WebCatalogProductResponse | null>(null);

	useEffect(() => {
		async function fetchProduct() {
			const productResponse: WebCatalogProductResponse = await VervAPI.getWebCatalogProduct(
				productID,
			);
			setProduct(productResponse);
		}
		void fetchProduct();
	}, [productID]);

	const priceString = productPrice({
		product,
		defaultPrice: defaults?.price,
		calculateFromIntro: !!fixedDiscountPercent,
	});

	const perDayPriceString = productPricePerDay({
		product,
		defaultPrice: pricePerDay({
			price: defaults?.price,
			days: defaults?.daysInterval,
		}),
		calculateFromIntro: productID === 'verv_3MONTH_25_99_USD_INTRO_7days_6_93_VERV',
	});

	return (
		<div key={productID} className={s.planOptionRegular__radio}>
			<Option
				className={classNames(
					s.option,
					coloredSelectedBorder && s.coloredSelectedBorder,
					isPrompted && s.optionMark,
					withPromocode && s.optionWithPromocode,
					promocodeAnimated && s.optionWithPromocode_noMargin,
					withSpecialOffer && s.optionWithSpecialOffer,
					isSelected && s.option__selected,
				)}
				optionValue={productID}
				save={save}
				doOnClick={() => doOnClick?.()}
			>
				{withPromocode && promocodeAnimated && (
					<div className={s.promocodeAnimated_wrapper}>
						<PromocodeAnimated />
					</div>
				)}
				{isPrompted && <div className={s.bestOffer}>{localizations.prompt}</div>}
				{withSpecialOffer && <div className={s.specialOffer}>{localizations.prompt}</div>}
				{withPromocode && !promocodeAnimated && (
					<>
						<div className={s.promocode}>
							<div className={s.promocode__title}>{localizations.promocodeText}</div>
							<div className={s.promocode__text}>
								{localizations.unlockedPromocodeText}
								<span className={s.promocode__text__promo}>{promocode}</span>
							</div>
							<img src={activatedPromocodeImg} alt='promo' />
						</div>
					</>
				)}
				<div className={s.optionColumn_wrapper}>
					<div className={isRadio ? s.optionColumn1_radio : s.optionColumn1_radio_spacer}></div>

					{optionStyle === 'v2' && (
						<div className={s.optionColumn1}>
							<div className={s.optionColumn1_top}>{localizations.planTitle}</div>
							{!hideDetailsPrice && (
								<div className={s.optionColumn1_bottomV2}>
									{showOldFixedPrice && (
										<div className={s.optionColumn1_oldPrice}>
											{currencyType} {oldFixedPrice}
										</div>
									)}
									<div className={s.optionColumn1_newPrice}>
										{currencyType} {priceString}
									</div>
								</div>
							)}
						</div>
					)}

					{optionStyle !== 'v2' && (
						<div className={s.optionColumn1}>
							<div className={s.optionColumn1_top}>{localizations.planTitle}</div>
							{!hideDetailsPrice && (
								<div className={s.optionColumn1_bottom}>
									{currencyType} {priceString} {localizations.perIntervalText}
								</div>
							)}
						</div>
					)}

					<div className={s.optionColumn2}>
						<div className={s.optionPrice}>
							<div
								className={s.optionPrice_dollar}
								style={currencyType === 'USD ' ? { fontSize: '10px' } : {}}
							>
								{currencyType}
							</div>
							<div className={s.optionPrice_firstPart}>{priceFirstPart(perDayPriceString)}</div>
							<div className={s.optionPrice_small}>
								<div className={s.optionPrice_secondPart}>{priceSecondPart(perDayPriceString)}</div>
								<div className={s.optionPrice_day}>{localizations.perDayText}</div>
							</div>
						</div>
					</div>
				</div>
			</Option>
		</div>
	);
});

export default PlanOption;
